import { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api from "@/api";
import {
  MapPin, User, Building2, Search, Loader2, AlertTriangle, Mail, Phone,
  ExternalLink, Hash, ChevronRight, Lightbulb, Sparkles,
} from "lucide-react";

const MODES = [
  { id: "smart",          label: "Smart search",   Icon: Sparkles,  placeholder: "Type anything — address, MP name, suburb, electorate, postcode…" },
  { id: "address",        label: "Address",        Icon: MapPin,    placeholder: "e.g. 14 Main North Road, Clare SA 5453" },
  { id: "representative", label: "Representative", Icon: User,      placeholder: "e.g. Anthony Albanese" },
  { id: "suburb",         label: "Suburb",         Icon: Building2, placeholder: "e.g. Norwood SA" },
  { id: "electorate",     label: "Electorate",     Icon: Hash,      placeholder: "e.g. Grayndler" },
];

const MODE_TO_API = {
  smart: "auto",
  address: "address",
  representative: "representative",
  suburb: "suburb",
  electorate: "electorate",
};

const EXAMPLES = {
  smart: ["71 Bristlebird Approach, Baldivis", "Albanese", "6171", "Grayndler"],
  address: ["14 Main North Road, Clare SA", "50 The Parade, Norwood SA", "1 Macquarie Street, Sydney"],
  representative: ["Anthony Albanese", "Peter Dutton", "Penny Wong"],
  suburb: ["Norwood SA", "Toorak VIC", "South Brisbane QLD"],
  electorate: ["Grayndler", "Sturt", "Cook"],
};

function ModeTab({ mode, active, onClick }) {
  const { Icon, label, id } = mode;
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`mode-${id}`}
      aria-pressed={active}
      className={[
        "flex-1 min-w-[120px] flex items-center justify-center gap-2 px-3 py-2.5 text-sm font-medium border-b-2 transition-all",
        active
          ? "border-[#1E3A8A] text-[#1E3A8A] bg-[#1E3A8A]/5"
          : "border-transparent text-gray-500 hover:text-[#1E3A8A] hover:bg-gray-50",
      ].join(" ")}
    >
      <Icon className="w-4 h-4" /> {label}
    </button>
  );
}

function levelLabel(level, house) {
  const lv = (level || "").toLowerCase();
  const hs = (house || "").toLowerCase();
  if (lv === "federal") return "Federal — House of Representatives";
  if (lv === "state_lower" || (lv === "state" && hs === "lower")) return "State — Lower House";
  if (lv === "state_upper" || (lv === "state" && hs === "upper")) return "State — Upper House";
  return [level, house].filter(Boolean).join(" ");
}

function levelAccent(level) {
  const lv = (level || "").toLowerCase();
  if (lv === "federal") return { bg: "bg-blue-50", border: "border-blue-200", text: "text-blue-800", chip: "bg-blue-100 text-blue-800" };
  if (lv.includes("upper")) return { bg: "bg-purple-50", border: "border-purple-200", text: "text-purple-800", chip: "bg-purple-100 text-purple-800" };
  return { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-800", chip: "bg-emerald-100 text-emerald-800" };
}

function MPCard({ mp }) {
  return (
    <div className="bg-white rounded-md border border-gray-200 p-3 space-y-1.5" data-testid={`mp-${mp.name}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="font-semibold text-gray-900">{mp.name}</div>
          <div className="text-xs text-gray-500">{mp.party}</div>
        </div>
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1.5 text-xs text-gray-700 pt-1">
        {mp.email && (
          <a href={`mailto:${mp.email}`} className="inline-flex items-center gap-1.5 hover:text-[#1E3A8A] hover:underline">
            <Mail className="w-3.5 h-3.5" /> {mp.email}
          </a>
        )}
        {mp.electorate_phone && (
          <a href={`tel:${mp.electorate_phone}`} className="inline-flex items-center gap-1.5 hover:text-[#1E3A8A]">
            <Phone className="w-3.5 h-3.5" /> {mp.electorate_phone}
            <span className="text-gray-400 text-[10px] uppercase tracking-wider">electorate</span>
          </a>
        )}
        {mp.parliament_phone && (
          <a href={`tel:${mp.parliament_phone}`} className="inline-flex items-center gap-1.5 hover:text-[#1E3A8A]">
            <Phone className="w-3.5 h-3.5" /> {mp.parliament_phone}
            <span className="text-gray-400 text-[10px] uppercase tracking-wider">parliament</span>
          </a>
        )}
        {mp.facebook && (
          <a href={mp.facebook} target="_blank" rel="noreferrer noopener" className="inline-flex items-center gap-1.5 hover:text-[#1E3A8A] hover:underline">
            <ExternalLink className="w-3.5 h-3.5" /> Facebook
          </a>
        )}
      </div>
    </div>
  );
}

function ResultCard({ result }) {
  const accent = levelAccent(result.level);
  return (
    <div className={`rounded-lg border ${accent.bg} ${accent.border} p-4 space-y-3`} data-testid={`result-${result.electorate}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className={`text-[11px] uppercase tracking-wider font-semibold ${accent.text}`}>
            {levelLabel(result.level, result.house)}
          </div>
          <div className="text-2xl font-bold text-gray-900 mt-0.5">{result.electorate}</div>
          <div className="text-sm text-gray-600">State: {result.state}</div>
        </div>
        <span className={`text-[11px] font-medium px-2 py-1 rounded-full ${accent.chip}`}>
          {result.match_quality === "exact" && "Exact match"}
          {result.match_quality === "geocode" && "Found by location"}
          {result.match_quality === "postcode" && `Postcode ${result.postcode || ""}`}
          {result.match_quality === "partial" && "Partial match"}
        </span>
      </div>
      {result.matched_address && (
        <div className="text-xs text-gray-500 italic">📍 {result.matched_address}</div>
      )}
      {result.mps && result.mps.length > 0 ? (
        <div className="space-y-2">
          <div className="text-xs font-medium text-gray-500 uppercase tracking-wider">
            Sitting member{result.mps.length > 1 ? "s" : ""}
          </div>
          {result.mps.map((mp, i) => <MPCard key={`${mp.name}-${i}`} mp={mp} />)}
        </div>
      ) : (
        <div className="text-xs text-gray-500 italic">No representative on record for this electorate.</div>
      )}
    </div>
  );
}

export default function GISTest() {
  const [mode, setMode] = useState("smart");
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, [mode]);

  const runSearch = async (q, m) => {
    const text = (q ?? query).trim();
    const useMode = m ?? mode;
    const apiMode = MODE_TO_API[useMode] || "auto";
    if (!text) { setError("Please enter a search term"); return; }
    setError(""); setLoading(true); setData(null);
    try {
      const res = await api.get(`/gis/search?q=${encodeURIComponent(text)}&mode=${apiMode}`);
      setData(res.data);
    } catch (err) {
      console.error(err);
      setError(err?.response?.data?.detail || "Search failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const onKey = (e) => { if (e.key === "Enter") runSearch(); };
  const onPickSuggestion = (s) => { setQuery(s.query); setMode(s.mode); runSearch(s.query, s.mode); };
  const onPickExample = (e) => { setQuery(e); runSearch(e, mode); };

  const current = MODES.find(m => m.id === mode);
  const hasResults = data && data.results && data.results.length > 0;
  const hasSuggestions = data && data.suggestions && data.suggestions.length > 0;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white p-4 sm:p-8">
      <div className="max-w-3xl mx-auto space-y-6">
        <Card className="shadow-lg" data-testid="gis-widget">
          <CardHeader className="border-b">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-md bg-[#1E3A8A]/10 flex items-center justify-center">
                <MapPin className="w-4 h-4 text-[#1E3A8A]" />
              </div>
              <div>
                <CardTitle className="text-xl sm:text-2xl text-gray-900">Electorate Lookup</CardTitle>
                <p className="text-xs text-gray-500 mt-0.5">
                  Type anything in <strong>Smart search</strong> — an address, MP name, suburb, electorate, or postcode — and it'll figure the rest out. Use the other tabs to lock the search to one type.
                </p>
              </div>
            </div>
          </CardHeader>

          <CardContent className="space-y-5 pt-5">
            {/* Mode tabs */}
            <div className="flex border-b border-gray-200 overflow-x-auto" data-testid="mode-tabs">
              {MODES.map(m => (
                <ModeTab key={m.id} mode={m} active={mode === m.id} onClick={() => { setMode(m.id); setData(null); setError(""); }} />
              ))}
            </div>

            {/* Search input */}
            <div className="flex gap-2">
              <Input
                ref={inputRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={onKey}
                placeholder={current?.placeholder}
                data-testid="search-input"
                className="flex-1"
              />
              <Button onClick={() => runSearch()} disabled={loading} data-testid="search-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Search className="w-4 h-4 mr-1" /> Search</>}
              </Button>
            </div>

            {/* Example chips */}
            <div className="flex flex-wrap gap-2" data-testid="examples">
              <span className="text-xs text-gray-500 mr-1 self-center">Try:</span>
              {(EXAMPLES[mode] || []).map((ex) => (
                <button
                  key={ex}
                  onClick={() => onPickExample(ex)}
                  className="text-xs px-2.5 py-1 bg-gray-100 hover:bg-[#1E3A8A]/10 hover:text-[#1E3A8A] rounded-full text-gray-600 transition-colors"
                  data-testid={`example-${ex.slice(0,12)}`}
                >
                  {ex}
                </button>
              ))}
            </div>

            {/* Error */}
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-md flex items-start gap-2" data-testid="error-banner">
                <AlertTriangle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                <span className="text-sm text-red-700">{error}</span>
              </div>
            )}

            {/* Notes from server (e.g. "no direct match — showing suggestions instead") */}
            {data?.notes?.length > 0 && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-md text-xs text-amber-800 space-y-1" data-testid="server-notes">
                {data.notes.map((n, i) => <div key={i}>• {n}</div>)}
              </div>
            )}

            {/* Results */}
            {hasResults && (
              <div className="space-y-3" data-testid="results">
                {data.results.map((r, i) => <ResultCard key={`${r.electorate}-${r.level}-${i}`} result={r} />)}
              </div>
            )}

            {/* No-results fallback with suggestions */}
            {data && !hasResults && (
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-md space-y-3" data-testid="no-results">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="font-semibold text-amber-900">No direct match found for "{data.query}"</div>
                    <div className="text-sm text-amber-800 mt-0.5">
                      Try one of the search modes above, or pick from the related results below.
                    </div>
                  </div>
                </div>
                {hasSuggestions && (
                  <div className="space-y-1.5" data-testid="suggestions">
                    <div className="text-xs font-medium text-amber-900 uppercase tracking-wider flex items-center gap-1.5">
                      <Lightbulb className="w-3.5 h-3.5" /> Did you mean…
                    </div>
                    {data.suggestions.map((s, i) => (
                      <button
                        key={`${s.label}-${i}`}
                        onClick={() => onPickSuggestion(s)}
                        className="w-full flex items-center justify-between gap-3 px-3 py-2 bg-white border border-amber-200 rounded-md hover:bg-amber-100 hover:border-amber-300 transition-colors text-left"
                        data-testid={`suggestion-${s.type}-${i}`}
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          {s.type === "representative" ? <User className="w-4 h-4 text-amber-700 flex-shrink-0" /> : <Hash className="w-4 h-4 text-amber-700 flex-shrink-0" />}
                          <div className="min-w-0">
                            <div className="text-sm font-medium text-gray-900 truncate">{s.label}</div>
                            <div className="text-xs text-gray-500 truncate">{s.context}</div>
                          </div>
                        </div>
                        <ChevronRight className="w-4 h-4 text-amber-600 flex-shrink-0" />
                      </button>
                    ))}
                  </div>
                )}
                {!hasSuggestions && (
                  <div className="text-sm text-amber-800">
                    Tip: try just the surname of an MP, the suburb without the state, or a 4-digit postcode.
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="bg-slate-50 border-slate-200">
          <CardContent className="pt-4">
            <h4 className="font-semibold text-sm text-slate-700 mb-2">How it works</h4>
            <ul className="text-xs text-slate-600 space-y-1.5">
              <li>• <strong>Address / Suburb</strong> — geocoded via OpenStreetMap, then mapped against official ABS POA 2021 boundaries.</li>
              <li>• <strong>Representative</strong> — searches our directory of every federal and state MP (House + Senate / Lower + Upper).</li>
              <li>• <strong>Electorate</strong> — finds the named electorate and lists every sitting member.</li>
              <li>• <strong>Postcode</strong> — type any 4-digit Australian postcode to find every electorate that touches it.</li>
            </ul>
            <p className="text-[11px] text-slate-400 mt-3">
              Data: Geoscape ABS POA 2021 boundaries · Civipen MP directory · OpenStreetMap Nominatim
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
